import type { Address } from 'viem';

import { erc20Abi, factoryAbi, marketAbi } from './abi';
import { readClient, readContractsBatch } from './client';
import { DEPLOYMENT, getActiveChainConfig, getActiveNetworkKey, NETWORK_STABLE_ADDRESSES } from './config';
import { fmtToken, shortAddr } from './format';
import { resolveCollateralMeta, resolveTokenLogoUrl, resolveTokenLogoUrlSync } from './collateral';
import { disconnectWalletSession, walletState } from './wallet';

type AccountModalOptions = {
  marketAddress?: Address;
  onWalletStateChange?: () => void | Promise<void>;
};

let activeOverlay: HTMLDivElement | null = null;
let activeCopyFeedback: HTMLDivElement | null = null;
const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';
const REFRESH_ICON_SVG = `<img src="${import.meta.env.BASE_URL}images/refreshing.svg" alt="" aria-hidden="true"/>`;
const CLOSE_ICON_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6l-12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function removeActiveModal(): void {
  if (activeOverlay) {
    activeOverlay.remove();
    activeOverlay = null;
  }
}

function fmtNative(amount: bigint): string {
  return (Number(amount) / 1e18).toLocaleString('en-US', { maximumFractionDigits: 4 });
}

/** Address-keyed logos for native/well-known tokens shown in the modal. */
const LOGO_BY_ADDRESS: Record<string, string> = {};

/** Symbol-keyed fallback logos for native tokens without addresses. */
const NATIVE_LOGOS: Record<string, string> = {
  PLS:  `${import.meta.env.BASE_URL}images/pls.png`,
  TPLS: `${import.meta.env.BASE_URL}images/pls.png`,
};

const FALLBACK_LOGO = `${import.meta.env.BASE_URL}images/coin.svg`;

/**
 * Build a token label with logo image and optional copy button.
 * Falls back to coin.svg when no logo is resolved.
 * @param symbol       Token symbol for display and fallback lookup.
 * @param address      ERC-20 token address — used for logo and copy button.  Pass null for native.
 * @param logoOverride Pre-resolved logo URL (skips async lookup, use sync path).
 */
function tokenLabelHtml(symbol: string, address: string | null = null, logoOverride?: string | null): string {
  const normalized = (symbol || 'TOKEN').toUpperCase();
  const logo = logoOverride
    ?? (address ? (resolveTokenLogoUrlSync(address) ?? LOGO_BY_ADDRESS[address.toLowerCase()]) : null)
    ?? NATIVE_LOGOS[normalized]
    ?? FALLBACK_LOGO;

  const canCopyAddress = Boolean(address && address !== ZERO_ADDRESS);
  const copyBtn = canCopyAddress
    ? `<button type="button" class="token-copy-btn" data-copy-address="${address}" aria-label="Copy ${normalized} token address" title="Copy ${normalized} token address">⧉</button>`
    : '';

  return `<span class="token-inline"><img src="${logo}" alt="${normalized}" class="token-logo"/>${normalized}${copyBtn}</span>`;
}

function formatPositionLine(
  yesBalance: bigint,
  noBalance: bigint,
  yesDecimals: number,
  noDecimals: number,
  yesSymbol: string,
  noSymbol: string,
): string {
  return `${yesSymbol} ${fmtToken(yesBalance, yesDecimals)} | ${noSymbol} ${fmtToken(noBalance, noDecimals)}`;
}

function formatClaimLine(payout: bigint, symbol: string): string {
  return `Claim ${fmtToken(payout)} ${symbol}`;
}

function showCopyFeedback(anchor: HTMLElement, message: string): void {
  if (activeCopyFeedback) {
    activeCopyFeedback.remove();
    activeCopyFeedback = null;
  }

  const feedback = document.createElement('div');
  feedback.className = 'token-copy-feedback';
  feedback.textContent = message;

  const rect = anchor.getBoundingClientRect();
  feedback.style.left = `${Math.round(rect.right + 6)}px`;
  feedback.style.top = `${Math.round(rect.top + rect.height / 2)}px`;

  document.body.appendChild(feedback);
  activeCopyFeedback = feedback;

  window.setTimeout(() => {
    if (activeCopyFeedback === feedback) {
      feedback.remove();
      activeCopyFeedback = null;
    }
  }, 1100);
}

export async function openAccountModal(options: AccountModalOptions = {}): Promise<void> {
  if (!walletState.account) return;

  const chain = getActiveChainConfig();

  removeActiveModal();

  const overlay = document.createElement('div');
  overlay.className = 'account-modal-overlay';
  overlay.innerHTML = `
    <div class="account-modal-card card" role="dialog" aria-modal="true" aria-label="Wallet account modal">
      <div class="account-modal-head">
        <div>
          <div class="account-modal-title">Wallet</div>
          <div class="account-modal-sub">Connected as ${shortAddr(walletState.account)}</div>
        </div>
        <button type="button" class="btn icon-btn icon-btn-close" data-action="close" aria-label="Close" title="Close">${CLOSE_ICON_SVG}</button>
      </div>

      <div class="account-modal-address mono">${walletState.account}</div>
      <a class="btn" href="${chain.blockExplorerUrls[0]}/address/${walletState.account}" target="_blank" rel="noreferrer">Open in Explorer</a>

      <div class="account-modal-balance-list">
        <div class="account-balance-row"><span class="mono">Network</span><span class="mono">${chain.chainName}</span></div>
        <div class="account-balance-row"><span class="mono">PLS Balance</span><span class="mono" data-field="pls">Loading...</span></div>
        <div class="account-balance-row"><span class="mono" data-field="collateral-label">Collateral Balance</span><span class="mono" data-field="usdc">Loading...</span></div>
        <div class="account-balance-row"><span class="mono" data-field="dai-label">DAI Balance</span><span class="mono" data-field="dai">Loading...</span></div>
      </div>

      <div class="account-modal-positions">
        <div class="account-modal-sub">Market Positions</div>
        <div class="account-position-list" data-field="positions">Loading...</div>
      </div>

      <div class="account-modal-actions">
        <button type="button" class="btn icon-btn account-modal-refresh-btn" data-action="refresh" aria-label="Refresh balances" title="Refresh">${REFRESH_ICON_SVG}</button>
        <button type="button" class="btn btn-red" data-action="disconnect">Disconnect</button>
      </div>
    </div>
  `;

  activeOverlay = overlay;
  document.body.appendChild(overlay);

  const closeBtn = overlay.querySelector('[data-action="close"]') as HTMLButtonElement;
  const refreshBtn = overlay.querySelector('[data-action="refresh"]') as HTMLButtonElement;
  const disconnectBtn = overlay.querySelector('[data-action="disconnect"]') as HTMLButtonElement;

  const plsField = overlay.querySelector('[data-field="pls"]') as HTMLSpanElement;
  const usdcField = overlay.querySelector('[data-field="usdc"]') as HTMLSpanElement;
  const collateralLabelField = overlay.querySelector('[data-field="collateral-label"]') as HTMLSpanElement;
  const daiLabelField = overlay.querySelector('[data-field="dai-label"]') as HTMLSpanElement;
  const daiField = overlay.querySelector('[data-field="dai"]') as HTMLSpanElement;
  const positionsField = overlay.querySelector('[data-field="positions"]') as HTMLDivElement;

  const closeModal = () => {
    removeActiveModal();
  };

  const refreshBalances = async () => {
    const account = walletState.account;
    if (!account) return;

    try {
      const networkKey = getActiveNetworkKey();
      // DAI address per network (from NETWORK_STABLE_ADDRESSES)
      const stableSet = NETWORK_STABLE_ADDRESSES[networkKey];
      const DAI_ADDRESSES: Record<string, string> = {
        mainnet: '0xefD766cCb38EaF1dfd701853BFCe31359239F305',
        testnet: '0x826e4e896CC2f5B371Cd7Bb0bd929DB3e3DB67c0',
      };
      const daiAddress = DAI_ADDRESSES[networkKey] ?? DAI_ADDRESSES.mainnet;
      // Don't show DAI row if DAI == collateral (already shown as Collateral Balance)
      const daiIsCollateral = daiAddress.toLowerCase() === DEPLOYMENT.collateral.toLowerCase();

      const [nativeBal, usdcBal, daiBal, collateralMeta, collateralLogo, daiLogo] = await Promise.all([
        readClient.getBalance({ address: account as Address }),
        readClient.readContract({
          address: DEPLOYMENT.collateral as Address,
          abi: erc20Abi,
          functionName: 'balanceOf',
          args: [account as Address],
        }),
        daiIsCollateral
          ? Promise.resolve(0n)
          : readClient.readContract({
            address: daiAddress as Address,
            abi: erc20Abi,
            functionName: 'balanceOf',
            args: [account as Address],
          }),
        resolveCollateralMeta(DEPLOYMENT.collateral),
        resolveTokenLogoUrl(DEPLOYMENT.collateral),
        resolveTokenLogoUrl(daiAddress),
      ]);

      collateralLabelField.textContent = `${collateralMeta.symbol} Balance`;
      plsField.innerHTML = `${fmtNative(nativeBal as bigint)} ${tokenLabelHtml(chain.nativeCurrency.symbol.toUpperCase())}`;
      usdcField.innerHTML = `${fmtToken(usdcBal as bigint, collateralMeta.decimals)} ${tokenLabelHtml(collateralMeta.symbol, DEPLOYMENT.collateral, collateralLogo)}`;
      if (daiIsCollateral) {
        daiLabelField.textContent = 'DAI Balance';
        daiField.textContent = '(same as collateral)';
      } else {
        daiLabelField.textContent = 'DAI Balance';
        daiField.innerHTML = `${fmtToken(daiBal as bigint, 18)} ${tokenLabelHtml('DAI', daiAddress, daiLogo)}`;
      }

      const marketCount = (await readClient.readContract({
        address: DEPLOYMENT.factory as Address,
        abi: factoryAbi,
        functionName: 'marketCount',
      })) as bigint;

      const totalMarkets = Number(marketCount);
      if (totalMarkets === 0) {
        positionsField.textContent = 'No markets found.';
        return;
      }

      const marketAddressCalls = Array.from({ length: totalMarkets }, (_, index) => ({
        address: DEPLOYMENT.factory as Address,
        abi: factoryAbi,
        functionName: 'markets',
        args: [BigInt(index)],
      }));
      const marketAddresses = (await readContractsBatch<Address | null>(marketAddressCalls))
        .filter((value): value is Address => Boolean(value));

      if (marketAddresses.length === 0) {
        positionsField.textContent = 'No markets found.';
        return;
      }

      const nowTs = Math.floor(Date.now() / 1000);
      const marketStateCalls = marketAddresses.flatMap((marketAddr) => [
        {
          address: marketAddr,
          abi: marketAbi,
          functionName: 'resolved',
        },
        {
          address: marketAddr,
          abi: marketAbi,
          functionName: 'resolveAfter',
        },
        {
          address: marketAddr,
          abi: marketAbi,
          functionName: 'collateral',
        },
      ]);
      const marketStateResults = await readContractsBatch<unknown>(marketStateCalls);

      const liveMarkets: Address[] = [];
      const resolvedMarkets: Address[] = [];
      const marketCollateralByAddress: Record<string, Address> = {};
      for (let i = 0; i < marketAddresses.length; i += 1) {
        const offset = i * 3;
        const resolved = Boolean(marketStateResults[offset]);
        const resolveAfter = Number(marketStateResults[offset + 1] ?? 0n);
        const marketAddr = marketAddresses[i];
        const collateralAddr = (marketStateResults[offset + 2] as Address | null) ?? null;
        if (collateralAddr) {
          marketCollateralByAddress[marketAddr.toLowerCase()] = collateralAddr;
        }
        if (resolved) {
          resolvedMarkets.push(marketAddr);
          continue;
        }
        if (resolveAfter > nowTs) {
          liveMarkets.push(marketAddr);
        }
      }

      const uniqueCollateralAddresses = Array.from(new Set(Object.values(marketCollateralByAddress).map((addr) => addr.toLowerCase())));
      const collateralMetaByAddress: Record<string, { decimals: number; symbol: string }> = {};
      if (uniqueCollateralAddresses.length > 0) {
        const collateralCalls: Array<{ address: Address; abi: typeof erc20Abi; functionName: 'decimals' | 'symbol' }> = [];
        for (const collateralAddress of uniqueCollateralAddresses) {
          collateralCalls.push({ address: collateralAddress as Address, abi: erc20Abi, functionName: 'decimals' });
          collateralCalls.push({ address: collateralAddress as Address, abi: erc20Abi, functionName: 'symbol' });
        }
        const collateralResults = await readContractsBatch<unknown>(collateralCalls);
        for (let i = 0; i < uniqueCollateralAddresses.length; i += 1) {
          const offset = i * 2;
          const rawDecimals = Number(collateralResults[offset] ?? collateralMeta.decimals);
          const decimals = Number.isFinite(rawDecimals) && rawDecimals >= 0 && rawDecimals <= 36 ? rawDecimals : collateralMeta.decimals;
          const symbol = String(collateralResults[offset + 1] ?? collateralMeta.symbol).trim() || collateralMeta.symbol;
          collateralMetaByAddress[uniqueCollateralAddresses[i]] = { decimals, symbol };
        }
      }

      const positionRows: string[] = [];

      if (resolvedMarkets.length > 0) {
        const quoteCalls = resolvedMarkets.map((marketAddr) => ({
          address: marketAddr,
          abi: marketAbi,
          functionName: 'quoteClaim',
          args: [account as Address],
        }));
        const quoteResults = await readContractsBatch<unknown>(quoteCalls);

        for (let i = 0; i < resolvedMarkets.length; i += 1) {
          const marketAddr = resolvedMarkets[i];
          const marketCollateralAddress = marketCollateralByAddress[marketAddr.toLowerCase()]?.toLowerCase() ?? '';
          const marketCollateral = collateralMetaByAddress[marketCollateralAddress] ?? collateralMeta;
          const quote = quoteResults[i] as readonly unknown[] | null;
          const canClaim = Boolean(quote?.[0]);
          const payout = typeof quote?.[1] === 'bigint' ? (quote[1] as bigint) : 0n;
          if (!canClaim || payout <= 0n) continue;
          const stagePill = '<span class="account-position-stage claimable">Claimable</span>';

          const focusTag = options.marketAddress && marketAddr.toLowerCase() === options.marketAddress.toLowerCase()
            ? '<span class="account-position-focus">current</span>'
            : '';

          positionRows.push(`
            <a class="account-position-row" href="?market=${marketAddr}" aria-label="Open market ${shortAddr(marketAddr)}">
              <span class="account-position-left mono">${shortAddr(marketAddr)} ${stagePill} ${focusTag}</span>
              <span class="mono">${formatClaimLine(payout, marketCollateral.symbol)}</span>
            </a>
          `);
        }
      }

      if (liveMarkets.length > 0) {
        const tokenAddressCalls = liveMarkets.flatMap((marketAddr) => [
          {
            address: marketAddr,
            abi: marketAbi,
            functionName: 'yesToken',
          },
          {
            address: marketAddr,
            abi: marketAbi,
            functionName: 'noToken',
          },
        ]);
        const tokenAddressResults = await readContractsBatch<unknown>(tokenAddressCalls);

        const tokenMetaCalls: Array<{ address: Address; abi: typeof erc20Abi; functionName: 'decimals' | 'symbol' }> = [];
        for (let i = 0; i < liveMarkets.length; i += 1) {
          const offset = i * 2;
          const yesTokenAddr = tokenAddressResults[offset] as Address;
          const noTokenAddr = tokenAddressResults[offset + 1] as Address;
          tokenMetaCalls.push({ address: yesTokenAddr, abi: erc20Abi, functionName: 'decimals' });
          tokenMetaCalls.push({ address: yesTokenAddr, abi: erc20Abi, functionName: 'symbol' });
          tokenMetaCalls.push({ address: noTokenAddr, abi: erc20Abi, functionName: 'decimals' });
          tokenMetaCalls.push({ address: noTokenAddr, abi: erc20Abi, functionName: 'symbol' });
        }
        const tokenMetaResults = await readContractsBatch<unknown>(tokenMetaCalls);

        const balanceCalls: Array<{ address: Address; abi: typeof erc20Abi; functionName: 'balanceOf'; args: [Address] }> = [];
        for (let i = 0; i < liveMarkets.length; i += 1) {
          const offset = i * 2;
          const yesTokenAddr = tokenAddressResults[offset] as Address;
          const noTokenAddr = tokenAddressResults[offset + 1] as Address;
          balanceCalls.push({ address: yesTokenAddr, abi: erc20Abi, functionName: 'balanceOf', args: [account as Address] });
          balanceCalls.push({ address: noTokenAddr, abi: erc20Abi, functionName: 'balanceOf', args: [account as Address] });
        }

        const balanceResults = await readContractsBatch<unknown>(balanceCalls);
        for (let i = 0; i < liveMarkets.length; i += 1) {
          const marketAddr = liveMarkets[i];
          const balanceOffset = i * 2;
          const metaOffset = i * 4;
          const marketCollateralAddress = marketCollateralByAddress[marketAddr.toLowerCase()]?.toLowerCase() ?? '';
          const marketCollateralDecimals = collateralMetaByAddress[marketCollateralAddress]?.decimals ?? collateralMeta.decimals;
          const yesBalance = BigInt((balanceResults[balanceOffset] as bigint) ?? 0n);
          const noBalance = BigInt((balanceResults[balanceOffset + 1] as bigint) ?? 0n);
          const yesDecimals = marketCollateralDecimals;
          const yesSymbol = String(tokenMetaResults[metaOffset + 1] ?? 'Y').trim() || 'Y';
          const noDecimals = marketCollateralDecimals;
          const noSymbol = String(tokenMetaResults[metaOffset + 3] ?? 'N').trim() || 'N';
          const stagePill = '<span class="account-position-stage live">Live</span>';

          const focusTag = options.marketAddress && marketAddr.toLowerCase() === options.marketAddress.toLowerCase()
            ? '<span class="account-position-focus">current</span>'
            : '';

          positionRows.push(`
            <a class="account-position-row" href="?market=${marketAddr}" aria-label="Open market ${shortAddr(marketAddr)}">
              <span class="account-position-left mono">${shortAddr(marketAddr)} ${stagePill} ${focusTag}</span>
              <span class="mono">${formatPositionLine(yesBalance, noBalance, yesDecimals, noDecimals, yesSymbol, noSymbol)}</span>
            </a>
          `);
        }
      }

      if (positionRows.length === 0) {
        positionsField.textContent = 'No live positions or claimable rewards found.';
      } else {
        positionsField.innerHTML = positionRows.join('');
      }
    } catch {
      plsField.textContent = 'Unavailable';
      usdcField.textContent = 'Unavailable';
      daiField.textContent = 'Unavailable';
      positionsField.textContent = 'Unavailable';
    }
  };

  closeBtn.addEventListener('click', closeModal);
  refreshBtn.addEventListener('click', () => {
    refreshBtn.classList.remove('is-spinning');
    void refreshBtn.offsetWidth;
    refreshBtn.classList.add('is-spinning');
    void refreshBalances();
  });

  refreshBtn.addEventListener('animationend', (event) => {
    if (event.animationName !== 'refresh-spin-once') return;
    refreshBtn.classList.remove('is-spinning');
  });

  disconnectBtn.addEventListener('click', async () => {
    disconnectWalletSession();
    if (options.onWalletStateChange) await options.onWalletStateChange();
    closeModal();
  });

  overlay.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const copyBtn = target.closest('.token-copy-btn') as HTMLButtonElement | null;
    if (copyBtn) {
      const tokenAddress = copyBtn.getAttribute('data-copy-address');
      if (tokenAddress) {
        void navigator.clipboard.writeText(tokenAddress)
          .then(() => showCopyFeedback(copyBtn, 'Copied'))
          .catch(() => showCopyFeedback(copyBtn, 'Copy failed'));
      }
      return;
    }

    if (event.target === overlay) closeModal();
  });

  void refreshBalances();
}
